<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Col,
		Container,
		ControlsContainer,
		EnergyDistribution,
		EnergyDistributionZendure,
		IconButton,
		Modal,
		Row,
		Toggle,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { getPVSetupDevices } from './pv_setup_helper';
	import { getPvSetupStatuses } from './pv_setup_live_calculate_helper';
	import { get_mode, is_gen_2, isProThreeEM } from 'src/helpers/interface_helpers';
	import { isTestUser } from 'src/helpers/user_helpers';
	//Components
	import LivePanelInfo from './LivePanelInfo.svelte';
	//Stores
	import { createMemberStatusSnapshot, createMemberSettingsSnapshot } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { real_id } from 'src/helpers/interface_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { PVSetupTypes } from './pv_setup_helper';

	export let pvSetupType: PVSetupTypes;

	const getDevicePower = (dev: Device, status, settings): number => {
		if (!is_gen_2(dev)) {
			if (dev.type == 'SHEM-3') {
				return (status?.emeters || [0]).map((i) => i.power).reduce((a, b) => a + b);
			}
			return status?.emeters?.[dev.channel]?.power || 0;
		} else if (
			isProThreeEM(dev.type) &&
			get_mode(dev, status, settings) !== 'monophase'
		) {
			return status?.['em:0']?.total_act_power || 0;
		} else if (status?.[`em1:${dev.channel}`]) {
			return status[`em1:${dev.channel}`]?.act_power;
		} else if (status?.[`switch:${dev.channel}`]) {
			return status[`switch:${dev.channel}`]?.apower;
		} else if (status?.[`pm1:${dev.channel}`]) {
			return status[`pm1:${dev.channel}`]?.apower;
		}
		return 0;
	};

	const getPvLiveStatus = (statuses, settingsSnapshot, pvSetupType: PVSetupTypes) => {
		let gridPower = 0;
		let generatedPower = 0;
		const pvSetupDevices = getPVSetupDevices(pvSetupType);
		pvSetupDevices.gridDevices.forEach((dev) => {
			gridPower += getDevicePower(dev, statuses[real_id(dev.id)], settingsSnapshot[real_id(dev.id)]) || 0;
		});
		pvSetupDevices.solarDevices.forEach((dev) => {
			generatedPower += getDevicePower(dev, statuses[real_id(dev.id)], settingsSnapshot[real_id(dev.id)]) || 0;
		});
		return getPvSetupStatuses(gridPower, generatedPower, pvSetupType, switchView);
	};

	let showModal = false;
	let switchView = false;

	// let gridPower = -500;
	// let generatedPower = 0;
	// $: pvData = getPvSetupStatuses(gridPower, generatedPower, pvSetupType);

	$: pvSetupDevices = getPVSetupDevices(pvSetupType);
	$: pvDeviceIds = [
		...pvSetupDevices.gridDevices,
		...pvSetupDevices.solarDevices,
	].map((dev) => dev.id);
	$: memberStatusStore = createMemberStatusSnapshot(pvDeviceIds);
	$: memberStatus = $memberStatusStore;
	$: memberSettingsStore = createMemberSettingsSnapshot(pvDeviceIds);
	$: memberSettings = $memberSettingsStore;
	$: pvData = getPvLiveStatus(memberStatus, memberSettings, pvSetupType);
</script>

{#key pvSetupType}
	<Row>
		<Col md="12" lg="8">
			<ControlsContainer
				class="height-full"
				orientation="vertical"
				color={$showNewLayout ? 'color-1' : undefined}
			>
				<svelte:fragment slot="first">
					<div style="pointer-events:none">
						{#if switchView}
							<EnergyDistributionZendure
								id="energy-distribution-zendure"
								variant="secondary"
								cards={pvData.secondCards}
								paths={pvData.secondPaths}
							/>
						{:else}
							<EnergyDistribution
								id="energy-distribution"
								cards={pvData.cards}
								paths={pvData.paths}
							/>
						{/if}
					</div>
				</svelte:fragment>
				<svelte:fragment slot="topRightSlot">
					{#if isTestUser()}
						<IconButton
							icon="fas fa-cog"
							aria-label={$format('button_pv_setup_switch_live_distributions', {
								default: 'Switch between live energy distribution views',
							})}
							on:click={() => (showModal = true)}
						/>
					{/if}
				</svelte:fragment>
			</ControlsContainer>
		</Col>
		<Col md="12" lg="4">
			<div class="grid">
				<LivePanelInfo {pvSetupType} {pvData} />
			</div>
		</Col>
	</Row>
{/key}

<Modal
	title="Live energy distributions"
	{showModal}
	removeScrollPadding
	on:close={() => (showModal = false)}
>
	<svelte:fragment slot="content">
		<Container padding="scrollPaddings">
			<Alert
				class="mb-l"
				type="warning"
				heading={'This functionality is under development and used for testing.'}
				message={'You can see this because you are using a test user account.'}
				isHeadingBold
			/>
			<Toggle class="mb-s" size="m" id="switch-between-live-energy" bind:checked={switchView}>
				{$format('button_pv_setup_switch_live_distributions', {
					default: 'Switch between live energy distribution views',
				})}
			</Toggle>
		</Container>
	</svelte:fragment>
</Modal>

<style>
	.grid {
		min-height: 100%;
		display: grid;
	}
</style>
